import type { ConsentManagerApi } from './types';

const HTMLElementBase = (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) as typeof HTMLElement;

const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;',
})[character] ?? character);

const mediaStyles = `
  :host { display: block; font-family: "Noto Sans JP", "Hiragino Sans", "Yu Gothic", sans-serif; }
  * { box-sizing: border-box; }
  .frame { position: relative; width: 100%; min-height: 360px; overflow: hidden; border: 1px solid #d8e1ec; border-radius: 16px; background: radial-gradient(circle at 16% 18%, rgba(0,194,203,.15), transparent 36%), linear-gradient(135deg, #f7faff, #edf3f8); }
  iframe { display: block; width: 100%; height: 450px; border: 0; }
  .placeholder { min-height: 360px; display: grid; place-items: center; padding: 32px 20px; text-align: center; }
  .content { max-width: 540px; }
  .map-mark { width: 48px; height: 48px; margin: 0 auto 14px; display: grid; place-items: center; border-radius: 15px; background: #0c2e60; color: #fff; font-size: 22px; box-shadow: 0 10px 25px rgba(12,46,96,.18); }
  h3 { margin: 0 0 8px; color: #0c2e60; font-size: 18px; }
  p { margin: 0 auto 18px; color: #536278; font-size: 13px; line-height: 1.8; }
  .actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 9px; }
  button { min-height: 44px; padding: 9px 15px; border: 1px solid #0c2e60; border-radius: 9px; background: #fff; color: #0c2e60; cursor: pointer; font: inherit; font-size: 13px; font-weight: 700; }
  button.primary { background: #0c2e60; color: #fff; }
  button:focus-visible { outline: 3px solid #f6c94c; outline-offset: 3px; }
  @media (max-width: 520px) { .frame, .placeholder { min-height: 320px; } .actions { flex-direction: column; } button { width: 100%; } }
`;

export class PrivacyExternalMediaElement extends HTMLElementBase {
  private sessionGranted = false;
  private unsubscribe: (() => void) | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    const ready = (): void => {
      this.unsubscribe?.();
      this.unsubscribe = window.privacyConsent?.on('consent:changed', () => {
        if (!this.manager?.hasConsent(this.category)) this.sessionGranted = false;
        this.render();
      }) ?? null;
      this.render();
    };
    if (window.privacyConsent) ready();
    else window.addEventListener('consent:ready', ready, { once: true });
  }

  disconnectedCallback(): void {
    this.unsubscribe?.();
  }

  private get manager(): ConsentManagerApi | undefined {
    return window.privacyConsent;
  }

  private get category(): string {
    return this.getAttribute('category') ?? 'external-media';
  }

  private render(): void {
    if (!this.shadowRoot) return;
    const allowed = this.sessionGranted || Boolean(this.manager?.hasConsent(this.category));
    const src = this.getAttribute('src') ?? '';
    const title = this.getAttribute('title') ?? '外部コンテンツ';
    const description = this.getAttribute('description') ?? `${title}を表示すると、外部サービスへの通信が発生します。`;
    const nonce = this.getAttribute('nonce');
    const styleTag = `<style${nonce ? ` nonce="${escapeHtml(nonce)}"` : ''}>${mediaStyles}</style>`;

    if (allowed && src) {
      const safe = (() => {
        try {
          const url = new URL(src, location.origin);
          return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
        } catch {
          return '';
        }
      })();
      this.shadowRoot.innerHTML = `${styleTag}<div class="frame"><iframe src="${escapeHtml(safe)}" title="${escapeHtml(title)}" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen></iframe></div>`;
      return;
    }

    this.shadowRoot.innerHTML = `${styleTag}
      <div class="frame placeholder">
        <div class="content">
          <div class="map-mark" aria-hidden="true">⌖</div>
          <h3>${escapeHtml(title)}</h3>
          <p>${escapeHtml(description)}</p>
          <div class="actions">
            <button type="button" data-once>今回のみ表示</button>
            <button type="button" class="primary" data-always>今後も表示を許可</button>
          </div>
        </div>
      </div>`;
    this.shadowRoot.querySelector('[data-once]')?.addEventListener('click', () => {
      this.sessionGranted = true;
      this.render();
    });
    this.shadowRoot.querySelector('[data-always]')?.addEventListener('click', () => {
      const current = this.manager?.getState()?.categories ?? {};
      void this.manager?.updatePreferences({ ...current, [this.category]: true }, 'feature').catch((error: unknown) => {
        console.error('[PrivacyConsent] 外部コンテンツの許可を保存できませんでした', error);
      });
    });
  }
}
